import { AgentSearchResult, AgentOffice, Agent } from "app/shared/model/Agent";
import { getLocalApi } from "./localApi";
import apiConfig from "app/api/apiConfig.json";
import { getAccessToken } from "app/auth/getAccessToken";
import { parse } from "cookie";
import { AuthenticationResult } from "@azure/msal-browser";

function getAdminApiBaseUrl(): string {
    if (import.meta.env.DEV) {
        return '/admin-api';
    }
    if (typeof window !== 'undefined' && window.location.hostname) {
        const hostname = window.location.hostname.toLowerCase();
        if (hostname.includes('pricing-admin-staging') || hostname.includes('staging')) {
            return 'https://pricing-staging.ecenglish.io/api';
        }
        if (hostname.includes('pricing-admin-dev') || hostname.includes('dev')) {
            return 'https://pricing-dev.ecenglish.io/api';
        }
    }
    
    // TODO: add production environment
    return 'https://pricing-dev.ecenglish.io/api';
}

async function getToken(forceRefresh: boolean = false): Promise<AuthenticationResult> {
    try {
        const tokenResult = await getAccessToken(undefined, forceRefresh);
        if (!tokenResult?.accessToken) {
            throw new Error('Failed to acquire access token');
        }
        return tokenResult;
    } catch (error: any) {
        if (error?.name === 'NotAuthenticatedError') {
            throw new Error('No active account. You must log in.');
        }
        throw new Error(`Token acquisition failed: ${error.message || error}`);
    }
}

function getSessionId() {
    const cookies = parse(document.cookie || '');
    return cookies['EcIoSession'] ?? '';
}

function buildHeaders(auth: AuthenticationResult | undefined, includeContentType = false): Record<string, string> {
    const headers: Record<string, string> = {
        'Accept': 'application/json',
        'X-EcIoSession': getSessionId()
    };

    if (includeContentType) {
        headers['Content-Type'] = 'application/json';
    }

    if (auth?.accessToken) {
        headers['Authorization'] = `Bearer ${auth.accessToken}`;
        const subKey = import.meta.env.VITE_APIM_SUBSCRIPTION_KEY;
        if (subKey) headers['Ocp-Apim-Subscription-Key'] = subKey;
    }

    headers['Cache-Control'] = 'no-cache';
    headers['Pragma'] = 'no-cache';
    return headers;
}

async function safeFetch(input: RequestInfo | URL, init?: RequestInit): Promise<Response> {
    try {
        return await fetch(input, init);
    } catch (e: any) {
        if (String(e).includes("Failed to fetch")) {
            throw new Error('Network error while contacting API.');
        }
        throw e;
    }
}

async function callAdminApi(url: string, method: string = 'GET', body?: any): Promise<Response> {
    const baseUrl = getAdminApiBaseUrl();
    const fullUrl = `${baseUrl}/${url.replace(/^\/+/, "")}`;
    
    let token: AuthenticationResult;
    try {
        token = await getToken(false);
    } catch (error: any) {
        throw new Error(`Authentication failed: ${error.message || error}`);
    }
    
    const headers = buildHeaders(token, !!body);
    
    if (!headers['Authorization']) {
        throw new Error('Authorization token is missing');
    }
    
    const response = await safeFetch(fullUrl, {
        method,
        body: body ? JSON.stringify(body) : undefined,
        headers,
        cache: 'no-store',
        credentials: 'include',
    });

    if (!response.ok) {
        const text = await response.text().catch(() => "");
        const errorMessage = text || response.statusText;
        
        if (response.status === 401) {
            throw new Error(`Unauthorized (401): Authentication failed. ${errorMessage ? `Details: ${errorMessage}` : 'Please check your credentials and try logging in again.'}`);
        }
        
        throw new Error(`Admin API request failed (HTTP ${response.status}) — ${errorMessage}`);
    }

    return response;
}

export async function searchAgents(searchTerm: string, onlyActive: boolean, maxRecords: number) : Promise<AgentSearchResult[]> {
    const localApi = await getLocalApi();

    return await localApi.fetchList(
        apiConfig.agents_search + "?q=" + encodeURIComponent(searchTerm) + '&onlyActive=' + onlyActive.toString() + '&maxRecords=' + maxRecords.toString(),
        mapAgentSearchResult,
        'Error fetching agents'
    );
}

export async function searchAgentsByType(agentType: "Agents" | "Internet Sales" | "Walk Ins", onlyActive: boolean = true): Promise<AgentSearchResult[]> {
    const localApi = await getLocalApi();
    
    const url = `${apiConfig.agents_search_by_type}?onlyActive=${onlyActive.toString()}&agentType=${encodeURIComponent(agentType)}`;
    
    try {
        return await localApi.fetchList(
            url,
            mapAgentSearchResult,
            'Error fetching agents by type'
        );
    } catch (error: any) {
        const response = await callAdminApi(url, 'GET');
        const data = await response.json();
        
        if (!Array.isArray(data)) {
            return [];
        }
        
        return data.map(mapAgentSearchResult);
    }
}

export async function getAgentsById(onlyActive: boolean, ... agentIds: string[]) : Promise<Agent[]> {
    const localApi = await getLocalApi();

    const requestObj = {
        onlyActive: false,
        agentOfficeIds: agentIds
    };

    return await localApi.call(
        apiConfig.agents_get_offices_by_id,
        'POST',
        JSON.stringify(requestObj))
        .then(response => {
            if (!response?.ok)
                throw new Error('Unable to fetch agents');
            return response.json().then((dtoObj: any) => {
                return dtoObj.map(mapAgent)
            })
        });
}

export async function getAgentOfficesById(agentOfficeIds: string[], onlyActive: boolean = true): Promise<Agent[]> {
    const requestObj = {
        onlyActive: onlyActive,
        agentOfficeIds: agentOfficeIds
    };

    try {
        const localApi = await getLocalApi();
        const response = await localApi.call(apiConfig.agents_get_offices_by_id, 'POST', JSON.stringify(requestObj));
        
        if (!response.ok) {
            throw new Error(`HTTP ${response.status}`);
        }
        
        const data = await response.json();
        
        if (!Array.isArray(data)) {
            return [];
        }
        
        return data.map(mapAgent);
    } catch (error: any) {
        try {
            const response = await callAdminApi(apiConfig.agents_get_offices_by_id, 'POST', requestObj);
            const data = await response.json();
            
            if (!Array.isArray(data)) {
                return [];
            }
            
            return data.map(mapAgent);
        } catch (adminError: any) {
            throw adminError;
        }
    }
}

function mapAgent(dto: any) : Agent {
    return dto;
}

function mapAgentSearchResult(dto: any) : AgentSearchResult {
    return dto;
}